<template>
  <article class="interactive-social-wall__post">
    <SocialWallAvatar :name="postItem.authorName" :photo="postItem.authorPhoto"
                      class="interactive-social-wall__post-avatar"/>

    <div class="interactive-social-wall__post-content">
      <div class="interactive-social-wall__post-header">
        <div class="interactive-social-wall__post-identity">
          <strong>{{ postItem.authorName }}</strong>
          <span v-if="postItem.roleLabel" class="interactive-social-wall__role">
            {{ postItem.roleLabel }}
          </span>
        </div>

        <div class="interactive-social-wall__post-meta">
          <time :datetime="postItem.post.created_at || ''">{{
              postItem.timeText
            }}
          </time>
          <div class="interactive-social-wall__post-menu" @click.stop>
            <button :aria-expanded="postItem.isMenuOpen"
                    :class="{
                'interactive-social-wall__post-menu-trigger--active':
                  postItem.isMenuOpen,
              }"
                    :disabled="postItem.isDeleting"
                    aria-haspopup="menu"
                    :aria-label="$t('general.post_options')"
                    class="interactive-social-wall__post-menu-trigger"
                    type="button"
                    @click="$emit('toggle-menu', postItem)"
                    @keydown.esc.stop="$emit('close-menu')">
              <span v-if="postItem.isDeleting" aria-hidden="true" class="interactive-social-wall__inline-spinner"/>
              <i v-else aria-hidden="true" class="uil-ellipsis-v"/>
            </button>

            <div v-if="postItem.isMenuOpen"
                 class="interactive-social-wall__post-menu-dropdown"
                 role="menu"
                 @keydown.esc.stop="$emit('close-menu')">
              <button class="interactive-social-wall__post-menu-item"
                      role="menuitem"
                      type="button"
                      @click="$emit('detail', postItem)">
                <i aria-hidden="true" class="uil-external-link-alt"/>
                <span>{{$t('general.goto_detail')}}</span>
                <i aria-hidden="true" class="uil-angle-right-b"/>
              </button>
              <button class="interactive-social-wall__post-menu-item"
                      role="menuitem"
                      type="button"
                      @click="$emit('show-likes', postItem)">
                <i aria-hidden="true" class="uil-heart"/>
                <span>{{$t('general.,show_liked_users')}}</span>
                <small>{{ postItem.likeCount }}</small>
              </button>
              <button v-if="postItem.canDelete"
                      class="interactive-social-wall__post-menu-item interactive-social-wall__post-menu-item--danger"
                      role="menuitem"
                      type="button"
                      @click="$emit('delete', postItem)"
              >
                <i aria-hidden="true" class="uil-trash-alt"/>
                <span>{{$t('general.delete_comment_post')}}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <p class="interactive-social-wall__post-text">{{ postItem.post.text }}</p>

      <div class="interactive-social-wall__post-actions">
        <span v-if="postItem.isPendingApproval"
              :title="$t('general.sent_to_admin_approval')"
              class="interactive-social-wall__approval-status"
              role="status"
        >
          <i aria-hidden="true" class="uil-clock-three"/>
          <span>{{ $t("general.sent_to_admin_approval") }}</span>
        </span>
          <button :aria-busy="postItem.isLikeUpdating"
                :aria-label="postItem.isLiked
                  ? $t('general.remove_like_count', { count: postItem.likeCount })
                  : $t('general.like_count', { count: postItem.likeCount })"
                :aria-pressed="postItem.isLiked"
                :class="{
            'interactive-social-wall__counter--active': postItem.isLiked,
          }"
                :disabled="postItem.isLikeUpdating || !postItem.isPersisted"
                :title="postItem.isLiked ? $t('general.remove_like') : $t('general.like')"
                class="interactive-social-wall__counter interactive-social-wall__counter--like"
                type="button"
                @click="$emit('toggle-like', postItem)"
        >
          <span v-if="postItem.isLikeUpdating"
                aria-hidden="true"
                class="interactive-social-wall__inline-spinner"
          />
          <i v-else aria-hidden="true" class="uil-thumbs-up"/>
          <span>{{ postItem.likeCount }}</span>
        </button>
        <button :aria-label="$t('general.open_comments_count', { count: postItem.commentCount })"
                :disabled="!postItem.isPersisted"
                class="interactive-social-wall__counter interactive-social-wall__counter--comment"
                :title="$t('general.open_comments')"
                type="button"
                @click="$emit('show-comments', postItem)"
        >
          <i aria-hidden="true" class="uil uil-comment"/>
          <span>{{ postItem.commentCount }}</span>
        </button>
      </div>

      <p v-if="postItem.actionError"
         class="interactive-social-wall__post-action-error"
         role="alert"
      >
        <i aria-hidden="true" class="uil-exclamation-circle"/>
        {{ postItem.actionError }}
      </p>
    </div>
  </article>
</template>

<script>
import UserProfileAvatar from "../../../../components/common/UserProfileAvatar.vue";

export default {
  name: "SocialWallPostItem",
  components: {
    SocialWallAvatar: UserProfileAvatar,
  },
  props: {
    postItem: {
      type: Object,
      required: true,
    },
  },
};
</script>

<style scoped>
.interactive-social-wall__post,
.interactive-social-wall__post * {
  box-sizing: border-box;
}

.interactive-social-wall__post button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  color: inherit;
  font: inherit;
  line-height: 1;
  cursor: pointer;
  background: transparent;
  border: 0;
  outline: none;
  box-shadow: none;
}

.interactive-social-wall__post button:disabled {
  cursor: not-allowed;
}

.interactive-social-wall__post {
  display: flex;
  min-height: 86px;
  gap: 14px;
  padding: 10px 0 9px;
  border-bottom: 1px solid #e5e9ef;
}

.interactive-social-wall__post:last-child {
  border-bottom: 0;
}

.interactive-social-wall__post-avatar {
  display: inline-flex;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  overflow: hidden;
  font-size: 12px;
  font-weight: 700;
  border-radius: 50%;
}

.interactive-social-wall__post-content {
  flex: 1;
  min-width: 0;
}

.interactive-social-wall__post-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 17px;
  gap: 8px;
}

.interactive-social-wall__post-identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  white-space: nowrap;
}

.interactive-social-wall__post-identity strong {
  overflow: hidden;
  color: #111827;
  font-size: 11.5px;
  font-weight: 700;
  line-height: 1.4;
  text-overflow: ellipsis;
}

.interactive-social-wall__role {
  flex: 0 0 auto;
  padding: 2px 8px;
  color: #50ad39;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.35;
  background: #eef8e9;
  border-radius: 10px;
}

.interactive-social-wall__post-meta {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
  color: #778195;
}

.interactive-social-wall__post-meta time {
  font-size: 9px;
  font-weight: 400;
  white-space: nowrap;
}

.interactive-social-wall__post-menu {
  position: relative;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
}

.interactive-social-wall__post-menu-trigger {
  width: 26px;
  height: 26px;
  margin-right: -6px !important;
  color: var(--iconColor) !important;
  border-radius: 6px;
  transition: color 150ms ease, background-color 150ms ease;
}

.interactive-social-wall__post-menu-trigger:hover,
.interactive-social-wall__post-menu-trigger:focus-visible,
.interactive-social-wall__post-menu-trigger--active {
  color: var(--iconColor) !important;
  background: #eff8ed !important;
}

.interactive-social-wall__post-menu-trigger:disabled {
  cursor: wait;
  opacity: 0.6;
}

.interactive-social-wall__post-menu-trigger > i {
  font-size: 17px;
}

.interactive-social-wall__post-menu-trigger
.interactive-social-wall__inline-spinner {
  width: 12px;
  height: 12px;
}

.interactive-social-wall__post-menu-dropdown {
  position: absolute;
  z-index: 30;
  top: calc(100% + 5px);
  right: -5px;
  width: 164px;
  padding: 5px;
  background: #ffffff;
  border: 1px solid #e0e5ec;
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.16);
}

.interactive-social-wall__post-menu-dropdown::before {
  position: absolute;
  top: -5px;
  right: 12px;
  width: 9px;
  height: 9px;
  background: #ffffff;
  border-top: 1px solid #e0e5ec;
  border-left: 1px solid #e0e5ec;
  content: "";
  transform: rotate(45deg);
}

.interactive-social-wall__post-menu-item {
  position: relative;
  z-index: 1;
  display: flex !important;
  width: 100%;
  min-height: 34px;
  justify-content: flex-start !important;
  gap: 8px;
  padding: 0 8px !important;
  color: #4e5a6d !important;
  font-size: 9px !important;
  font-weight: 500 !important;
  text-align: left;
  border-radius: 6px;
  transition: color 150ms ease, background-color 150ms ease;
}

.interactive-social-wall__post-menu-item:hover,
.interactive-social-wall__post-menu-item:focus-visible {
  color: var(--iconColor) !important;
  background: #f0f8ee !important;
}

.interactive-social-wall__post-menu-item > i:first-child {
  flex: 0 0 22px;
  font-size: 15px;
  text-align: center;
}

.interactive-social-wall__post-menu-item > span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.interactive-social-wall__post-menu-item > i:last-child:not(:first-child) {
  flex: 0 0 auto;
  color: #98a1af;
  font-size: 13px;
}

.interactive-social-wall__post-menu-item > small {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  min-width: 19px;
  height: 18px;
  padding: 0 5px;
  color: #4cae39;
  font-size: 8px;
  font-weight: 700;
  background: #edf8ea;
  border-radius: 9px;
}

.interactive-social-wall__post-menu-item--danger {
  --iconColor: #b84d4d;
  margin-top: 3px !important;
  color: #ad4545 !important;
  border-top: 1px solid #f0e3e3 !important;
  border-radius: 0 0 6px 6px;
}

.interactive-social-wall__post-menu-item--danger:hover,
.interactive-social-wall__post-menu-item--danger:focus-visible {
  --iconColor: #a13d3d;
  color: #a13d3d !important;
  background: #fff1f1 !important;
}

.interactive-social-wall__post-text {
  max-width: 100%;
  margin: 4px 0 6px;
  overflow: hidden;
  color: #161b26;
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.45;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.interactive-social-wall__post-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 28px;
  gap: 5px;
  color: #728095;
  white-space: nowrap;
}

.interactive-social-wall__approval-status {
  --iconColor: #b7791f;
  display: inline-flex;
  min-width: 0;
  min-height: 23px;
  align-items: center;
  gap: 4px;
  margin-right: auto;
  padding: 3px 7px;
  overflow: hidden;
  color: #9a6518;
  font-size: 8.5px;
  font-weight: 600;
  line-height: 1.25;
  background: #fff7e6;
  border: 1px solid #f2d49c;
  border-radius: 6px;
}

.interactive-social-wall__approval-status > i {
  flex: 0 0 auto;
  font-size: 12px;
}

.interactive-social-wall__approval-status > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.interactive-social-wall__counter {
  display: inline-flex;
  min-width: 34px;
  min-height: 27px;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0 6px !important;
  color: #68758b !important;
  font-size: 9.5px;
  line-height: 1;
  border-radius: 6px;
  transition: color 160ms ease, background-color 160ms ease;
}

.interactive-social-wall__counter i {
  font-size: 18px;
}

.interactive-social-wall__counter:hover,
.interactive-social-wall__counter:focus-visible {
  color: var(--iconColor) !important;
  background: color-mix(in srgb, var(--iconColor) 16%, #ffffff) !important;
}

.interactive-social-wall__counter:disabled {
  cursor: wait;
  opacity: 0.62;
}

.interactive-social-wall__counter--like,
.interactive-social-wall__counter--comment {
  color: var(--iconColor);
}

.interactive-social-wall__counter--comment:disabled {
  cursor: default;
}

.interactive-social-wall__counter--active {
  color: var(--iconColor) !important;
  background: color-mix(in srgb, var(--iconColor) 16%, #ffffff) !important;
}

.interactive-social-wall__inline-spinner {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 1.5px solid rgba(104, 117, 139, 0.32);
  border-top-color: #50b83e;
  border-radius: 50%;
  animation: interactive-social-wall-spin 700ms linear infinite;
}

.interactive-social-wall__post-action-error {
  --iconColor: #d84f4f;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 4px 0 0;
  color: #c74444;
  font-size: 8.5px;
  line-height: 1.35;
}

.interactive-social-wall__post-action-error i {
  flex: 0 0 auto;
  font-size: 12px;
}

@keyframes interactive-social-wall-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 480px) {
  .interactive-social-wall__post {
    gap: 10px;
  }

  .interactive-social-wall__post-actions {
    gap: 8px;
  }
}
</style>
